import { useEffect, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Select,
  MenuItem,
  Button,
  Chip,
  FormHelperText,
  useTheme,
} from "@mui/material";
import { tokens } from "../../theme";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { BugPriority, BugReport } from "../../store/slice/bug-fix/types";
import apiService from "../../service/Api";
import type {
  PaginatedUsersResponse,
  User,
} from "../../store/slice/user-management/types";
import { usePermissions } from "../../hooks/usePermissions";
import { PERMISSIONS } from "../../constants/permissions";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";

type AssignmentPanelProps = {
  bug?: BugReport | null;
  assigning?: boolean;
  existingAssigneeId?: number | null;
  onAssign: (payload: {
    bug: BugReport;
    developerId: number;
    priority: BugPriority;
    dueDate?: string;
  }) => Promise<void> | void;
};

const priorities: { label: string; value: BugPriority }[] = [
  { label: "Low", value: "low" },
  { label: "Medium", value: "medium" },
  { label: "High", value: "high" },
  { label: "Urgent", value: "urgent" },
];

const AssignmentPanel = ({
  bug,
  assigning = false,
  onAssign,
  existingAssigneeId,
}: AssignmentPanelProps) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { hasPermission } = usePermissions();

  const [developers, setDevelopers] = useState<User[]>([]);
  const [loadingDevelopers, setLoadingDevelopers] = useState(false);
  const [developersError, setDevelopersError] = useState<string | null>(null);
  const [priority, setPriority] = useState<BugPriority>("medium");
  const [error, setError] = useState<string | null>(null);
  const [developerId, setDeveloperId] = useState<string>("");
  const [dueDate, setDueDate] = useState<string>("");

  useEffect(() => {
    if (developers.length > 0) {
      setDeveloperId(existingAssigneeId ? String(existingAssigneeId) : "");
    }
  }, [developers, existingAssigneeId]);

  useEffect(() => {
    setDueDate(bug?.due_date ? bug.due_date.slice(0, 10) : "");
  }, [bug]);
  useEffect(() => {
    let isMounted = true;

    const fetchDevelopers = async () => {
      setLoadingDevelopers(true);
      setDevelopersError(null);

      try {
        const responseData = (
          await apiService.get<PaginatedUsersResponse>(
            "/api/authentication/users/?role=dev",
          )
        ).data;

        if (!isMounted) return;

        setDevelopers(responseData.results);
      } catch {
        if (!isMounted) return;

        setDevelopers([]);
        setDevelopersError("Failed to load developers.");
      } finally {
        if (isMounted) {
          setLoadingDevelopers(false);
        }
      }
    };

    fetchDevelopers();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSubmit = async () => {
    if (!hasPermission(PERMISSIONS.BUG_CHANGE)) return;
    if (!bug) return;
    if (!developerId) {
      setError("Please select a developer.");
      return;
    }
    setError(null);
    await onAssign({
      bug,
      developerId: Number(developerId),
      priority,
      dueDate: dueDate || undefined,
    });
  };
  return (
    <Box
      sx={{
        backgroundColor: colors.primary[900],
        borderRadius: "16px",
        p: 2.5,
        width: "100%",
        my: 3,
      }}
    >
      <Stack spacing={2}>
        {/* Assigned Developer */}
        <Box>
          <Typography
            variant="caption"
            fontWeight={600}
            letterSpacing={0.5}
            mb={0.5}
          >
            {existingAssigneeId ? "RE-ASSIGN DEVELOPER" : "ASSIGNED DEVELOPER"}
          </Typography>

          <Select
            fullWidth
            size="small"
            displayEmpty
            value={developerId}
            onChange={(event) => {
              setDeveloperId(String(event.target.value));
              setError(null);
            }}
            IconComponent={KeyboardArrowDownIcon}
            sx={{
              backgroundColor: colors.primary[900],
              borderRadius: "10px",
            }}
            disabled={loadingDevelopers}
          >
            <MenuItem value="">
              <em>
                {loadingDevelopers
                  ? "Loading developers..."
                  : "Select developer here"}
              </em>
            </MenuItem>
            {developers.map((dev) => {
              return (
                <MenuItem key={dev.id} value={String(dev.id)}>
                  {dev.first_name} {dev.last_name}
                </MenuItem>
              );
            })}
          </Select>
          {error && (
            <FormHelperText sx={{ color: "error.main", mt: 0.5 }}>
              {error}
            </FormHelperText>
          )}
          {!error && developersError && (
            <FormHelperText sx={{ color: "error.main", mt: 0.5 }}>
              {developersError}
            </FormHelperText>
          )}
        </Box>

        {/* Priority */}
        <Box gap={3}>
          <Typography
            sx={{
              marginBottom: 2,
            }}
          >
            What&apos;s the priority for this task?
          </Typography>

          <Stack direction="row" spacing={1}>
            {priorities.map((priorityOption) => (
              <Chip
                key={priorityOption.value}
                label={priorityOption.label}
                clickable
                color={
                  priority === priorityOption.value ? "primary" : undefined
                }
                onClick={() => setPriority(priorityOption.value)}
                sx={{
                  backgroundColor:
                    priority === priorityOption.value
                      ? "rgba(120, 72, 55, 1)"
                      : colors.primary[900],
                  borderRadius: "999px",
                  px: 1.5,
                  fontWeight: 500,
                  border:
                    priority === priorityOption.value
                      ? "1px solid rgba(120, 72, 55, 1)"
                      : "1px solid transparent",
                }}
              />
            ))}
          </Stack>
        </Box>

        {/* Due Date */}
        <Box>
          <Typography
            variant="caption"
            fontWeight={600}
            letterSpacing={0.5}
            mb={0.5}
            sx={{ display: "block" }}
          >
            DUE DATE
          </Typography>
          <DatePicker
            label="Due date"
            value={dueDate ? dayjs(dueDate) : null}
            onChange={(value) =>
              setDueDate(value ? dayjs(value).format("YYYY-MM-DD") : "")
            }
            minDate={dayjs().startOf("day")}
            slotProps={{
              textField: {
                fullWidth: true,
                size: "small",
                sx: {
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    backgroundColor: colors.primary[900],
                  },
                },
              },
            }}
          />
        </Box>

        {/* Actions */}
        <Stack direction="row" spacing={2} mt={1}>
          {/* <Button
            fullWidth
            variant="contained"
            sx={{
              backgroundColor: "rgba(241, 245, 249, 1)",
              color: "rgba(51, 65, 85, 1)",
              textTransform: "none",
              borderRadius: "10px",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "rgba(226, 232, 240, 1)",
                boxShadow: "none",
              },
            }}
          >
            Send a reminder
          </Button> */}

          <Button
            fullWidth
            variant="contained"
            onClick={handleSubmit}
            disabled={
              !bug || assigning || !hasPermission(PERMISSIONS.BUG_CHANGE)
            }
            sx={{
              backgroundColor: "rgba(120, 72, 55, 1)", // brown
              color: colors.primary[900],
              textTransform: "none",
              borderRadius: "10px",
              boxShadow: "none",
              "&:hover": {
                backgroundColor: "rgba(99, 60, 46, 1)",
                boxShadow: "none",
              },
            }}
          >
            {assigning
              ? "Assigning..."
              : existingAssigneeId
                ? "Re-assign Developer"
                : "Assign Developer"}
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
};

export default AssignmentPanel;
